import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {ApiTokenCreateMutation, ApiTokenListQuery, ApiTokenRevokeMutation} from './graphqlQueries';
import type {ApiTokenListQueryQuery, CreateApiTokenInput} from '~/graphql-react-query/graphql';

export type ApiToken = NonNullable<
    NonNullable<ApiTokenListQueryQuery['apiToken']>['apiTokenList']
>[number];

export type CreateApiTokenResult = {
    token: ApiToken;
    secret: string;
};

const apiTokenQueryKeys = {
    list: () => ['apiTokenList'] as const
};

export function useApiTokenList() {
    const request = useClientMainAuthorizedFetch();

    return useQuery({
        queryKey: apiTokenQueryKeys.list(),
        queryFn: async () => {
            const response = await request(ApiTokenListQuery, {input: {}});
            const tokens = response.apiToken?.apiTokenList;
            if (!tokens) throw new Error('API token list not returned from API');

            return tokens.filter((token) => token.revokedAt == null);
        }
    });
}

export function useApiTokenCreate() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: CreateApiTokenInput): Promise<CreateApiTokenResult> => {
            const response = await request(ApiTokenCreateMutation, {input});
            const created = response.apiToken?.createApiToken;
            if (!created) throw new Error('API token not returned after create');

            const {apiToken: secret, ...token} = created;
            if (!secret) throw new Error('API token secret not returned after create');

            return {token, secret};
        },
        onSuccess(result) {
            queryClient.setQueryData<ApiToken[]>(apiTokenQueryKeys.list(), (current) => {
                const existing = current ?? [];
                return [result.token, ...existing.filter((token) => token.id !== result.token.id)];
            });
        }
    });
}

export function useApiTokenRevoke() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await request(ApiTokenRevokeMutation, {input: {id}});
            const revoked = response.apiToken?.revokeApiToken;
            if (!revoked) throw new Error('API token not returned after revoke');
            return revoked;
        },
        onSuccess(revoked) {
            queryClient.setQueryData<ApiToken[]>(apiTokenQueryKeys.list(), (current) =>
                (current ?? []).filter((token) => token.id !== revoked.id)
            );
        }
    });
}
